import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentUuid,
  createAgent,
  deleteAgent,
  duplicateAgent,
  updateAgent,
  type Agent,
  type AgentInput,
} from "@/modules/chat/queries/agents-queries";
import { skillsQueryOptions, skillLabel } from "@/modules/chat/queries/skills-queries";
import {
  connectorsQueryOptions,
  connectorLabel,
} from "@/modules/chat/queries/connectors-queries";
import {
  agentCommandsQueryOptions,
  commandsQueryOptions,
} from "@/modules/chat/queries/commands-queries";
import { spacesListQueryOptions, personalSpaceQueryOptions } from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { apiFetch } from "@/shared/api";
import {
  ChatStoreProvider,
  useChatStore,
  useChatStoreApi,
} from "@/modules/chat/stores";
import { useChatActions } from "@/modules/chat/hooks/use-chat-actions";
import { ChatActionsProvider } from "@/modules/chat/components/ChatActionsContext";
import { MessageList } from "@/modules/chat/components/MessageList";
import { InputBar } from "@/modules/chat/components/InputBar";
import { AgentIcon } from "./AgentIcon";
import { AgentSection, type SectionItem } from "./AgentSection";
import { slugify } from "./slugify";

interface Props {
  mode: "new" | "edit";
  selected: Agent | null;
  onClose: () => void;
}

interface FormState {
  label: string;
  description: string;
  system_prompt: string;
  skill_ids: string[];
  connector_ids: string[];
  command_refs: string[];
  model: string;
  /** `null` = personal scope, string = team space id. */
  spaceId: string | null;
}

const EMPTY_FORM: FormState = {
  label: "",
  description: "",
  system_prompt: "",
  skill_ids: [],
  connector_ids: [],
  command_refs: [],
  model: "",
  spaceId: null,
};

const SECTION_HELP =
  "Если ничего не выбрано — у агента не будет доступа. Чтобы дать доступ, добавьте конкретные пункты — агент сможет использовать только их.";

/**
 * Two-pane builder: form on the left, test chat on the right. The test
 * session is provisioned on mount and the form rides along in
 * runtimeForwardedProps.test_agent — backend rebuilds the agent from
 * that snapshot per turn so prompt edits take effect without saving.
 *
 * The whole subtree mounts under its own ChatStoreProvider so the test
 * session is fully isolated from the main chat.
 */
export function AgentBuilder(props: Props) {
  return (
    <ChatStoreProvider devtoolsName="strands-chat-test">
      <AgentBuilderInner {...props} />
    </ChatStoreProvider>
  );
}

function AgentBuilderInner({ mode, selected, onClose }: Props) {
  const qc = useQueryClient();
  const storeApi = useChatStoreApi();
  const { data: skills = [] } = useQuery(skillsQueryOptions());
  const { data: connectors = [] } = useQuery(connectorsQueryOptions());
  const { data: commands = [] } = useQuery(commandsQueryOptions());
  // For an existing agent, fetch its resolved command set (includes
  // foreign user-personal commands attached by other space members —
  // the "gateway through agent" model). `commands` (the personal list)
  // is still used as the source of *available* picks.
  const { data: agentResolvedCommands = [] } = useQuery(
    agentCommandsQueryOptions(mode === "edit" ? selected?.ref ?? null : null),
  );
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const activeSpaceId = useSpaceStore((s) => s.currentSpaceId);

  const isSystem = selected?.scope === "system";
  const readOnly = isSystem;

  // Seed the new-form scope from the sidebar's active space — same logic
  // as ConnectorBuilder. `spacesListQueryOptions()` already filters out
  // the personal space, so any active id missing from the list falls
  // back to personal (null).
  const seedSpaceId = useMemo(() => {
    if (!activeSpaceId) return null;
    return spaces.some((s) => s.id === activeSpaceId) ? activeSpaceId : null;
  }, [activeSpaceId, spaces]);

  const [form, setForm] = useState<FormState>(() => {
    if (mode === "edit" && selected) {
      return {
        label: selected.label || selected.name,
        description: selected.description,
        system_prompt: selected.system_prompt,
        skill_ids: [...selected.skill_ids],
        connector_ids: [...(selected.connector_ids ?? [])],
        command_refs: [...(selected.command_refs ?? [])],
        model: selected.model ?? "",
        spaceId: selected.space_id ?? selected.space?.id ?? null,
      };
    }
    return { ...EMPTY_FORM, spaceId: seedSpaceId };
  });

  // Re-seed scope when /spaces resolves after the dialog opened. Only
  // touches "new" mode and only while the user hasn't picked yet — once
  // the user has interacted with the select (including picking
  // "Личное" → null), the seed must not snap their choice back.
  const userPickedSpaceRef = useRef(false);
  useEffect(() => {
    if (mode !== "new") return;
    if (userPickedSpaceRef.current) return;
    if (form.spaceId !== null) return;
    if (seedSpaceId !== null) {
      setForm((f) => ({ ...f, spaceId: seedSpaceId }));
    }
  }, [mode, seedSpaceId, form.spaceId]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [testSessionId, setTestSessionId] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    setBootError(null);
    (async () => {
      try {
        const resp = await apiFetch("/api/sessions/test", { method: "POST" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: { id: string } = await resp.json();
        if (aborted) return;
        storeApi.setState((s) => {
          s.activeSessionId = data.id;
        });
        setTestSessionId(data.id);
      } catch (e) {
        if (!aborted) setBootError((e as Error).message || "Не удалось создать тестовую сессию");
      }
    })();
    return () => {
      aborted = true;
    };
  }, [storeApi]);

  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => {
    storeApi.getState().setRuntimeForwardedProps({ test_agent: form });
  }, [form, storeApi]);

  const chatActions = useChatActions();

  const chatActionsValue = useMemo(
    () => ({
      registerTool: chatActions.registerTool,
      sendToolResult: chatActions.sendToolResult,
      respondToInterrupt: chatActions.respondToInterrupt,
    }),
    [
      chatActions.registerTool,
      chatActions.sendToolResult,
      chatActions.respondToInterrupt,
    ],
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agents"] });
    qc.invalidateQueries({ queryKey: ["agent-commands"] });
  };

  const createMutation = useMutation({
    mutationFn: (input: AgentInput) => createAgent(input),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e: Error) => setErrorText(humanizeError(e.message)),
  });
  const updateMutation = useMutation({
    mutationFn: ({ uuid, input }: { uuid: string; input: AgentInput }) => updateAgent(uuid, input),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e: Error) => setErrorText(humanizeError(e.message)),
  });
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteAgent(uuid),
    onSuccess: () => { invalidate(); onClose(); },
  });
  const duplicateMutation = useMutation({
    mutationFn: (ref: string) => duplicateAgent(ref),
    onSuccess: () => { invalidate(); onClose(); },
  });

  // Skill and connector pickers are scoped to the agent's tenancy:
  // system entries are always available; user entries only when their
  // home space matches the agent's. Without this filter `scope=all`
  // leaks every visible row into the editor, and a personal agent ends
  // up showing team-space items it can't legitimately load (the
  // backend silently skips them at resolve time, but the UI was
  // promising they'd work).
  const targetSpaceId = form.spaceId ?? personalSpace?.id ?? null;
  const skillItems: SectionItem[] = useMemo(
    () => skills
      .filter((s) =>
        s.scope === "system"
          || (targetSpaceId !== null && s.space?.id === targetSpaceId),
      )
      .map((s) => ({
        id: s.ref,
        label: skillLabel(s),
        hint: s.description,
      })),
    [skills, targetSpaceId],
  );
  const connectorItems: SectionItem[] = useMemo(
    () =>
      connectors
        .filter((c) => targetSpaceId !== null && c.space?.id === targetSpaceId)
        .map((c) => ({
          id: c.id,
          label: connectorLabel(c),
          hint: c.enabled ? c.description : `${c.description ?? ""} (выключен)`.trim(),
          disabled: !c.enabled,
        })),
    [connectors, targetSpaceId],
  );
  // Available picks: caller's own personal + system commands.
  const commandItems: SectionItem[] = useMemo(
    () => commands.map((c) => ({
      id: c.ref,
      label: c.label || c.id,
      hint: c.description,
    })),
    [commands],
  );
  // Selected chips: merge resolved (covers foreign refs) with the
  // personal list, then pick the ones in `form.command_refs`. Foreign
  // refs that no longer resolve (deleted command) just stay as bare ref
  // chips so the user can see and remove them.
  const commandLookup = useMemo(() => {
    const m = new Map<string, { label: string; hint: string }>();
    commands.forEach((c) => m.set(c.ref, { label: c.label || c.id, hint: c.description }));
    agentResolvedCommands.forEach((c) =>
      m.set(c.ref, { label: c.label || c.id, hint: c.description }),
    );
    return m;
  }, [commands, agentResolvedCommands]);

  const selectedSkills = skillItems.filter((s) => form.skill_ids.includes(s.id));
  const selectedConnectors = connectorItems.filter((c) => form.connector_ids.includes(c.id));
  const selectedCommands: SectionItem[] = form.command_refs.map((ref) => {
    const found = commandLookup.get(ref);
    return {
      id: ref,
      label: found?.label ?? ref,
      hint: found?.hint ?? "Команда недоступна",
    };
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave = form.label.trim().length > 0 && !isPending;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrorText(null);
  }

  function handleSave() {
    setErrorText(null);
    const label = form.label.trim();
    if (!label) return;
    const name = mode === "edit" && selected ? selected.name : slugify(label);
    const input: AgentInput = {
      name,
      label,
      description: form.description.trim(),
      system_prompt: form.system_prompt,
      skill_ids: form.skill_ids,
      connector_ids: form.connector_ids,
      command_refs: form.command_refs,
      model: form.model.trim() || null,
      space_id: form.spaceId,
    };
    if (mode === "new") createMutation.mutate(input);
    else if (selected) {
      const uuid = agentUuid(selected);
      if (uuid) updateMutation.mutate({ uuid, input });
    }
  }

  function handleDelete() {
    if (!selected) return;
    const uuid = agentUuid(selected);
    if (!uuid) return;
    if (!confirm(`Удалить агента «${form.label || selected.name}»?`)) return;
    deleteMutation.mutate(uuid);
  }

  const title =
    mode === "new"
      ? "Новый агент"
      : selected?.label || selected?.name || "Агент";

  return (
    <div className="agent-builder">
      <header className="cmd-builder-topbar">
        <button
          type="button"
          className="cmd-builder-back"
          onClick={onClose}
          aria-label="Назад"
        >
          <ArrowLeftIcon />
        </button>
        <span className="cmd-builder-topbar-icon">
          <AgentIcon size={24} variant={selected?.scope ?? "user"} />
        </span>
        <span className="cmd-builder-topbar-title">{title}</span>
      </header>

      <div className="agent-builder-body">
        <section className="agent-builder-form">
          <p className="cmd-builder-intro">
            Агент — это настроенный AI-помощник: системный промпт, набор навыков и коннекторов.
          </p>

          {isSystem && selected && (
            <div className="cmd-builder-banner">
              <span>Системный агент — редактирование недоступно.</span>
              <button
                type="button"
                className="cmd-page-create"
                onClick={() => duplicateMutation.mutate(selected.ref)}
                disabled={duplicateMutation.isPending}
              >
                Сделать копию
              </button>
            </div>
          )}

          {errorText && <div className="cmd-builder-error">{errorText}</div>}

          <div className="cmd-builder-fields">
            <Field label="Название" htmlFor="agent-label">
              <input
                type="text"
                id="agent-label"
                name="agent-label"
                className="cmd-builder-input"
                value={form.label}
                onChange={(e) => update("label", e.target.value)}
                placeholder="Например: «Помощник по документам»."
                readOnly={readOnly}
                maxLength={120}
                autoComplete="off"
              />
            </Field>

            <Field label="Описание" htmlFor="agent-description">
              <textarea
                id="agent-description"
                name="agent-description"
                className="cmd-builder-textarea cmd-builder-textarea--short"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder={
                  "Кратко опишите, для каких задач предназначен агент и кому он будет полезен.\n\n" +
                  "Например: «Помогает сотрудникам находить ответы в корпоративных документах»."
                }
                readOnly={readOnly}
                rows={4}
              />
            </Field>

            <Field
              label="Пространство"
              htmlFor="agent-space"
              hint={
                form.spaceId
                  ? "Агент будет доступен всем участникам этого пространства."
                  : "Личный агент — виден только вам."
              }
            >
              <select
                id="agent-space"
                className="cmd-builder-input"
                value={form.spaceId ?? ""}
                onChange={(e) => {
                  const next = e.target.value === "" ? null : e.target.value;
                  userPickedSpaceRef.current = true;
                  // Drop connector picks that don't belong to the new
                  // scope — a personal connector can't follow the agent
                  // into a team space and vice versa.
                  const nextTarget = next ?? personalSpace?.id ?? null;
                  setForm((f) => ({
                    ...f,
                    spaceId: next,
                    connector_ids: f.connector_ids.filter((id) => {
                      const c = connectors.find((x) => x.id === id);
                      return !!c && c.space?.id === nextTarget;
                    }),
                  }));
                  setErrorText(null);
                }}
                disabled={readOnly}
              >
                <option value="">Личное</option>
                {spaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Инструкции" htmlFor="agent-prompt">
              <textarea
                id="agent-prompt"
                name="agent-prompt"
                className="cmd-builder-textarea"
                value={form.system_prompt}
                onChange={(e) => update("system_prompt", e.target.value)}
                placeholder={
                  "Опишите, как агент должен помогать пользователям: его роль, стиль ответов и ограничения.\n\n" +
                  "Например: «Ты — помощник по работе с документами. Отвечай кратко, используй загруженные материалы и сообщай, если информации недостаточно»."
                }
                readOnly={readOnly}
                rows={14}
              />
            </Field>

            <AgentSection
              title="Навыки"
              helpText={SECTION_HELP}
              available={skillItems}
              selected={selectedSkills}
              emptyAvailableHint="Нет доступных навыков"
              onAdd={(id) => update("skill_ids", [...form.skill_ids, id])}
              onRemove={(id) => update("skill_ids", form.skill_ids.filter((x) => x !== id))}
              disabled={readOnly}
            />

            <AgentSection
              title="Коннекторы"
              helpText={SECTION_HELP}
              available={connectorItems}
              selected={selectedConnectors}
              emptyAvailableHint="Нет коннекторов в этом пространстве"
              onAdd={(id) => update("connector_ids", [...form.connector_ids, id])}
              onRemove={(id) => update("connector_ids", form.connector_ids.filter((x) => x !== id))}
              disabled={readOnly}
            />

            <AgentSection
              title="Команды"
              helpText={SECTION_HELP}
              available={commandItems}
              selected={selectedCommands}
              emptyAvailableHint="Нет команд — создайте их на странице «Команды»"
              onAdd={(id) => update("command_refs", [...form.command_refs, id])}
              onRemove={(id) => update("command_refs", form.command_refs.filter((x) => x !== id))}
              disabled={readOnly}
            />
          </div>

          {!readOnly && (
            <div className="cmd-builder-actions">
              <button
                type="button"
                className="cmd-page-create"
                onClick={handleSave}
                disabled={!canSave}
              >
                {isPending
                  ? "Сохранение…"
                  : mode === "new"
                    ? "Создать"
                    : "Сохранить"}
              </button>
              <button
                type="button"
                className="cmd-builder-cancel"
                onClick={onClose}
              >
                Отмена
              </button>
              {mode === "edit" && selected && (
                <button
                  type="button"
                  className="cmd-builder-delete"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  Удалить
                </button>
              )}
            </div>
          )}
        </section>

        <ChatActionsProvider value={chatActionsValue}>
          <TestPanel
            sessionId={testSessionId}
            bootError={bootError}
            sendMessage={chatActions.sendMessage}
            stopStreaming={chatActions.stopStreaming}
            commandRefs={form.command_refs}
            onClear={async () => {
              try {
                const resp = await apiFetch("/api/sessions/test", { method: "POST" });
                if (!resp.ok) return;
                const data: { id: string } = await resp.json();
                storeApi.setState((s) => {
                  s.activeSessionId = data.id;
                  s.messages = [];
                  s.partsMap = {};
                  s.streaming = false;
                  s.appState = {};
                  s.inReasoning = false;
                  s.toolCallArgsBuffer = {};
                });
                setTestSessionId(data.id);
              } catch {
                /* swallow */
              }
            }}
          />
        </ChatActionsProvider>
      </div>
    </div>
  );
}

interface TestPanelProps {
  sessionId: string | null;
  bootError: string | null;
  sendMessage: (
    text: string,
    overrideSid?: string,
    command?: { label: string },
  ) => Promise<void>;
  stopStreaming: () => void;
  commandRefs: string[];
  onClear: () => Promise<void>;
}

function TestPanel({ sessionId, bootError, sendMessage, stopStreaming, commandRefs, onClear }: TestPanelProps) {
  const isStreaming = useChatStore((s) => s.streaming);
  const messageCount = useChatStore((s) => s.messages.length);
  return (
    <aside className="agent-test-pane">
      <header className="agent-test-pane-header">
        <span className="agent-test-pane-title">Тестирование агента</span>
        {messageCount > 0 && (
          <button
            type="button"
            className="agent-test-pane-clear"
            onClick={onClear}
            disabled={isStreaming}
          >
            Очистить
          </button>
        )}
      </header>
      {bootError ? (
        <div className="agent-test-pane-error">
          Не удалось подготовить тестовый чат: {bootError}.
        </div>
      ) : !sessionId ? (
        <div className="agent-test-pane-loading">Готовим тестовую сессию…</div>
      ) : (
        <>
          <div className="agent-test-pane-messages">
            <MessageList activeId={sessionId} />
          </div>
          <div className="agent-test-pane-input">
            <InputBar
              onSend={(text, command) => sendMessage(text, sessionId, command)}
              onStop={stopStreaming}
              disabled={false}
              isStreaming={isStreaming}
              sessionId={sessionId}
              folderConnected={false}
              folderName={null}
              folderPickerSupported={false}
              onPickFolder={async () => {}}
              onDisconnectFolder={() => {}}
              heroMode={false}
              syncStatus={{ active: false, direction: "", current: 0, total: 0, warning: "" }}
              commandRefs={commandRefs}
              hideFooter
            />
          </div>
        </>
      )}
    </aside>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="cmd-builder-field" htmlFor={htmlFor}>
      <span className="cmd-builder-field-label">{label}</span>
      {children}
      {hint && <span className="cmd-builder-field-hint">{hint}</span>}
    </label>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3 5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function humanizeError(raw: string): string {
  if (/conflict|already exists|409/i.test(raw)) {
    return "Агент с таким названием уже существует. Измените название.";
  }
  return raw || "Не удалось сохранить агента.";
}
